/**
 * projectsBoardConfig.ts — the BoardConfig the EMBEDDED `EngineBoardView` consumers read
 * (the Client Hub's Projects section, the dashboard ProjectsWidget, the legacy
 * `/operations/projects` mount) until they switch to the BSG Projects board — plus the
 * small model-derived helpers every OTHER surface uses to show a project's stage.
 *
 * BSG Projects (2026-09-24): DERIVED from the project model
 * (`projectPipelineConfig.ts`) — never a hand-copied stage list, so it cannot drift.
 *
 *   stages — each phase's forward stages in path order (Training at the 4-call maximum,
 *            so every training stage has a column), the phase's side stage straight
 *            after them, then the off-board buckets: Future · Canceled · Invalid · Done.
 *   groups — the four phases (label + colour from the model), then one group per
 *            off-board bucket. The off-board groups are TERMINAL: none of them is open
 *            work (`PROJECT_NOT_OPEN_STATUSES`), so the engine keeps them out of its
 *            "active" counts and sorts them last.
 *
 * Labels are VERBATIM from the model. A stage has no colour of its own in the model — it
 * wears its phase's. The colours stay literal hex from the model's data because they feed
 * MUI `alpha()` derivations downstream (calendar chips, timeline bars), which throw on
 * `var(...)`.
 */
import type { BoardConfig, GroupConfig, StageConfig } from '../engine/dataLayers/types';
import {
  LEGACY_PROJECT_STAGE_MAP,
  MAX_TRAINING_CALLS,
  PROJECT_NOT_OPEN_STATUSES,
  PROJECT_OFF,
  PROJECT_OFF_BY_ID,
  PROJECT_PHASES,
  PROJECT_PHASE_BY_ID,
  PROJECT_STAGE_BY_KEY,
  firstStageOfType,
  isProjectDone,
  isProjectTypeKey,
  phaseStages,
  projectStatusLabel,
  type ProjectTypeKey,
} from './projectPipelineConfig';

function buildProjectsBoardConfig(): BoardConfig {
  const stages: StageConfig[] = [];
  const groups: GroupConfig[] = [];
  let sequence = 0;
  for (const phase of PROJECT_PHASES) {
    const line = [...phaseStages(phase.id, MAX_TRAINING_CALLS), ...(phase.side ? [phase.side] : [])];
    for (const s of line) {
      stages.push({ key: s.key, label: s.label, color: phase.color, sequence: ++sequence, group: phase.id });
    }
    groups.push({ id: phase.id, label: phase.label, color: phase.color, stage_keys: line.map((s) => s.key) });
  }
  for (const off of PROJECT_OFF) {
    stages.push({ key: off.id, label: off.label, color: off.color, sequence: ++sequence, group: off.id });
    groups.push({ id: off.id, label: off.label, color: off.color, stage_keys: [off.id], is_terminal: true, narrow: true });
  }
  return {
    stages,
    groups,
    // Slim the create-card modal to Title / Client / Stage / Assignee (the
    // Client field carries contacts; the rest stay editable in the detail modal).
    createFormFields: ['client'],
    createFormHideContacts: true,
    // The detail modal's Details list: Project due (projects.due_date — the promise
    // to the client), Deliver by, then the three calls. Description lives in its own
    // rail tile (CD-20 A3); the client, sprint and workflow are rail tiles too.
    detailsFields: ['due_date', 'deliver_by', 'documentation_call', 'delivery_call', 'training_call'],
  };
}

export const PROJECTS_BOARD_CONFIG: BoardConfig = buildProjectsBoardConfig();

// ── Showing a stored status anywhere else ──────────────────────────────────

/** Is `status` a key the model can name — a stage, an off-board bucket, or a legacy key? */
function isNamedStatus(status: string): boolean {
  return !!(PROJECT_STAGE_BY_KEY[status] || PROJECT_OFF_BY_ID[status] || LEGACY_PROJECT_STAGE_MAP[status]);
}

/**
 * The label any surface shows for a stored project status: `projectStatusLabel`'s words
 * (a legacy key keeps its old label), but NEVER a raw key — a status the model cannot
 * name reads "Unsorted", the board's word for a stage it can't place (README §4).
 * No status at all reads "No stage".
 */
export function projectStageLabel(status: string | null | undefined): string {
  if (!status) return projectStatusLabel(status);
  return isNamedStatus(status) ? projectStatusLabel(status) : 'Unsorted';
}

/**
 * The colour a stored status wears: a stage its phase's, an off-board bucket its own,
 * a legacy key the colour of the stage it maps to. Null when the model can't place it —
 * the caller keeps its own neutral.
 */
export function projectStageColor(status: string | null | undefined): string | null {
  if (!status) return null;
  const stage = PROJECT_STAGE_BY_KEY[status];
  if (stage) return PROJECT_PHASE_BY_ID[stage.phase]?.color ?? null;
  if (PROJECT_OFF_BY_ID[status]) return PROJECT_OFF_BY_ID[status].color;
  const legacy = LEGACY_PROJECT_STAGE_MAP[status];
  return legacy?.to ? projectStageColor(legacy.to) : null;
}

/** The fields the open predicate reads — every project row carries them. */
export interface ProjectOpenInput {
  status?: string | null;
  project_type?: string | null;
  training_call_count?: number | null;
  completed_at?: string | null;
}

/**
 * Open work: `completed_at IS NULL AND status NOT IN PROJECT_NOT_OPEN_STATUSES` — the
 * predicate every "open projects" count uses — plus the close stage of the project's
 * type, for a row that reaches the client before `completed_at` is stamped. A Canceled,
 * Invalid or Future project is never open; neither is a Done one.
 */
export function isProjectOpen(p: ProjectOpenInput): boolean {
  if (p.completed_at) return false;
  if (p.status && PROJECT_NOT_OPEN_STATUSES.includes(p.status)) return false;
  return !isProjectDone({ status: p.status, project_type: p.project_type ?? null, training_call_count: p.training_call_count });
}

/**
 * The stage a brand-new project starts on: its type's first stage ("It starts on"),
 * or New Custom Project Requested when nothing says what kind of work it is.
 */
export function newProjectStatus(type?: string | null, calls?: number | null): string {
  const t: ProjectTypeKey = isProjectTypeKey(type) ? type : 'custom';
  return firstStageOfType(t, calls);
}
